import { isProductReleased, releaseToString, daysUntilRelease, daysBetweenDates } from 'utils';
import format from 'date-fns/format';
import { t }  from 'datastore/i18n';

const WAREHOUSES = [992, 993];
const STOCK_STATUS_CL_OK = 1;
const STOCK_STATUS_CL_EMPTY = 2;
const STOCK_STATUS_CANT_DELIVER_TO_STORE = 3;

const methods = {
	isReleased (release) {
		// Return true if no relase is specified.
		if (!release) {
			return true;
		}

		return isProductReleased(release);
	},
	releaseDate (release) {
		if (!release) {
			return "";
		}
		return releaseToString(release);
	},
	/**
	 * Returns an object with info about the time remaining until we have a
	 * web stock delivery.
	 *
	 * Null if there is no delivery in progress.
	 */
	timeToWebStockDelivery (stock) {
		// We only get the delivery time when an order is confirmed.
		if (
			stock.hasOwnProperty("orders") &&
			stock.orders.hasOwnProperty("CL") &&
			stock.orders.CL.hasOwnProperty("confirmed")
		) {
			const days = [0, 0];
			let date = null;
			const time = stock.orders.CL.delivery_time;

			if (typeof time[0] === "number") {
				days[0] = time[0];
				days[1] = time[0];
				if (time.length > 1) {
					days[1] = time[1];
				} else if (time[0] > 1) {
					days[1] = time[0] + 1;
				}
			} else if (typeof time[0] === "string") {
				date = new Date(time[0]);
				const now = new Date();
				const d = daysBetweenDates(date, now);
				days[0] = days[1] = d;
			}
			return { days, date };
		}
		return null;
	},
	daysToInternalDelivery (store, daysSinceSent, userSettings) {
		if (daysSinceSent >= 1) {
			if (
				userSettings.storeGroupFormat[0].stores.includes(store) ||
				store === 5 /* Uppsala is handled like a Stockholm store for number of days */
			) {
				return [0, 1];
			}
			return [1, 2];
		}

		if (
			userSettings.storeGroupFormat[0].stores.includes(store) ||
			store ===
				5 /* Uppsala is handled like a Stockholm store for number of days */
		) {
			return [1, 2];
		}
		return [2, 3];
	},
	daysToDelivery (store, stock, userSettings) {
		let inCentralStock = false;
		for (const warehouse of WAREHOUSES) {
			if (stock.webStock[warehouse] >= 2) {
				inCentralStock = true;
				break;
			}
		}
		if (inCentralStock) {
			if (
				userSettings.storeGroupFormat[0].stores.includes(store) ||
				store === 5 /* Uppsala is handled like a Stockholm store for number of days */
			) {
				return [1, 3];
			}
			return [2, 3];
		}
		return null;
	},
	inSupplierStock (stock) {
		return stock.hasOwnProperty("supplier") && stock.supplier > 0;
	},
	stockClass (store, stock, stockStatus, release) {
		const qty = stock[store];
		if (release && !this.isReleased(release)) {
			if (daysUntilRelease(release) < 2 && qty) {
				return {
					"stock-icon-hype": true,
				};
			}

			return {
				"stock-icon-notreleased": true,
			};
		} else if (qty > 0) {
			if (store === "supplier") {
				return {
					"stock-icon-in-supplier-stock": true,
				};
			}
			return {
				"stock-icon-instock": true,
			};
		} else if (stockStatus === STOCK_STATUS_CL_EMPTY) {
			return {
				"stock-icon-outofstock": true,
			};
		} else {
			return {
				"stock-icon-outofstock-incentral": true,
			};
		}
	},

	calculateStockStatus (store, stock, userSettings, release, size, productStatus = [], cart = []) {
		if (productStatus && productStatus.indexOf(11/* Mail when available*/) >= 0) {
			const ret = {
				stockStatus: 1,
				stockText: '',
				stockTextExtra: '',
			};
			ret.stockClass = {
				'stock-icon-notreleased': true,
			};
			return ret;
		} else if (cart.length) {
			return this.calculateCart(cart, store, userSettings, release, size);
		} else {
			const ret = this.calculateStockText(store, stock, userSettings, release, size);
			ret.stockClass = this.stockClass(store, stock, ret.stockStatus, release);
			return ret;
		}
	},

	calculateStockText (store, stock, userSettings, release, size) {
		const retObj = {
			stockText: "",
			stockTextExtra: "",
			stockStatus: STOCK_STATUS_CL_OK,
			stockClass: {},
		};

		if (store === "download") {
			retObj.stockText = t("STOCK.IN_STOCK");
			return retObj;
		}

		const qty = stock[store];
		const displayCap = stock.displayCap || 50;

		if (release !== null && !this.isReleased(release)) {
			// Check how many days until release, less than two we need to start hyping, display the quantity if we have any.
			retObj.stockText = this.releaseDate(release).toString();
			if (daysUntilRelease(release) < 2 && qty) {
				if (qty > displayCap) {
					retObj.stockText += " (" + displayCap + "+ st" + ")";
					return retObj;
				}

				if (qty > 0) {
					retObj.stockText += " (" + qty + " st" + ")";
					return retObj;
				}
			}

			return retObj;
		}

		if (qty > displayCap) {
			retObj.stockText =
				size === "large"
					? displayCap + "+ st"
					: "Fler än " + displayCap + " st";
			return retObj;
		}

		if (qty > 0) {
			retObj.stockText = size === "large" ? qty + " st" : qty + " st";
			return retObj;
		}
		if (stock.hasOwnProperty("orders")) {
			// Check if we have had a delivery to this store!
			if (
				stock.hasOwnProperty("orders") &&
				stock.orders.hasOwnProperty(store)
			) {
				const daysToDelivery = this.daysToInternalDelivery(
					store,
					stock.orders[store].days_since,
					userSettings
				);
				let amount = stock.orders[store].amount;
				if (amount < 0) {
					amount = "<" + -1 * amount;
				} else if (amount > displayCap) {
					amount = displayCap + "+";
				}

				retObj.stockTextExtra = "(" + amount + " st på väg)";
				retObj.stockText =
					daysToDelivery[0] + "-" + daysToDelivery[1] + " dagar";
				return retObj;
			}

			if (stock.orders.hasOwnProperty("CL")) {
				// Check web stock
				if (!stock[1] && !stock.orders.CL.ordered) {
					// If store isn't web and there is nothing in stock at CL
					if (store !== "web") {
						retObj.stockStatus = STOCK_STATUS_CL_EMPTY;
						retObj.stockText = "Ej i lager";
						return retObj;
					}

					// If the product isn't availible at the CL and the CL has not ordered and the saldo warning is -1
					if (
						stock.orders.CL.status === -1 ||
						(this.inSupplierStock(stock) && !stock.orders.CL.status)
					) {
						retObj.stockStatus = STOCK_STATUS_CL_EMPTY;
						retObj.stockText = "Beställningsvara";
						return retObj;
					}
				}

				if (store === "web") {
					let confirmed = false;
					let timestr = "";

					// We only get the delivery time when an order is confirmed.
					const deliveryInfo = this.timeToWebStockDelivery(stock);
					if (deliveryInfo) {
						confirmed = true;

						const daysToStock = deliveryInfo.days;
						const deliveryDate = deliveryInfo.date;

						if (daysToStock && daysToStock[0] > 7) {
							// Show a date.
							timestr = format(deliveryDate, "YYYY-MM-DD");
						} else if (daysToStock[0] === daysToStock[1]) {
							// Fixed number of days.
							if (daysToStock[0] > 1) {
								timestr = daysToStock[0] + " dagar";
							} else {
								timestr = daysToStock[0] + " dag";
							}
						} else {
							// Estimate number of days.
							timestr = daysToStock[0] + "-" + daysToStock[1] + " dagar";
						}
					}

					let count = stock.orders.CL.ordered;
					if (count > displayCap) {
						count = displayCap + "+";
					} else if (count < 0) {
						count = "<" + -1 * count;
					}

					retObj.stockTextExtra = "(" + count + " st på väg)";

					if (confirmed) {
						retObj.stockText = timestr;
						return retObj;
					}

					if (confirmed) {
						retObj.stockStatus = STOCK_STATUS_CL_OK;
					} else {
						retObj.stockStatus = STOCK_STATUS_CL_EMPTY;
					}
					retObj.stockText = "Ej i lager";

					return retObj;
				}
			}
		}

		const daysToDelivery = this.daysToDelivery(store, stock, userSettings);
		if (! daysToDelivery) {
			retObj.stockStatus = STOCK_STATUS_CL_EMPTY;
			retObj.stockText = "Ej i lager";
			return retObj;
		}

		retObj.stockText = daysToDelivery[0] + "-" + daysToDelivery[1] + " dagar";
		return retObj;
	},

	calculateCart (cart, store, userSettings, release, size) {
		let hasUnshippableProduct = false;
		let stockStatus = 0;
		let calculatedStockObject = {};
		let allProductsInStore = true;

		for (const row of cart) {
			/**
			 * If a product is out of stock in specifik store and also
			 * discontinued, the product wont ever arrive in that store
			 */
			if (row.product.discontinued && ! row.product.stock[store]) {
				hasUnshippableProduct = true;
			}

			// Need a temporary const to control when we change the return objects values
			const tempRet = this.calculateStockText(store, row.product.stock, userSettings, release, size);
			tempRet.stockClass = this.stockClass(store, row.product.stock, calculatedStockObject.stockStatus, release);

			// To set initial values
			if (allProductsInStore) {
				calculatedStockObject = tempRet;
				calculatedStockObject.stockClass = tempRet.stockClass;
			} else if (tempRet.stockStatus > STOCK_STATUS_CL_OK) { // Id product is not in CL, we wan't to override previous values
				calculatedStockObject = tempRet;
				calculatedStockObject.stockClass = tempRet.stockClass;
			}

			/**
			 * If a products is out of stock in store but in CL, we dont want to override
			 * this value by mistake
			 */
			if (tempRet.stockClass.hasOwnProperty('stock-icon-outofstock-incentral')) {
				allProductsInStore = false;
				calculatedStockObject.stockClass = tempRet.stockClass;
			}

			// Since the lowest status has the least issues, only change if the value goes up
			if (tempRet.stockStatus > stockStatus) {
				stockStatus = tempRet.stockStatus;
			}
		}

		// Specific status for unshippable products that are discontinued
		if (hasUnshippableProduct && stockStatus > STOCK_STATUS_CL_OK) {
			stockStatus = STOCK_STATUS_CANT_DELIVER_TO_STORE;
			calculatedStockObject = {
				stockStatus: STOCK_STATUS_CANT_DELIVER_TO_STORE,
				stockText: 'Denna order kan ej hämtas upp i denna butik.',
				stockClass: {
					'stock-icon-outofstock': true,
				}
			};
		} else if (calculatedStockObject.stockText !== 'Ej i lager') { // Old string building logic from store-item
			if (calculatedStockObject.stockClass.hasOwnProperty('stock-icon-instock')) {
				calculatedStockObject.stockText = 'direkt!';
			} else if (calculatedStockObject.stockClass.hasOwnProperty('stock-icon-outofstock-incentral')) {
				calculatedStockObject.stockText = 'om ' + calculatedStockObject.stockText;
			}
			calculatedStockObject.stockText = 'Redo att hämtas ' + calculatedStockObject.stockText;
		}

		return calculatedStockObject;
	},
};

export default methods;
